import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Check, AlertCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Header } from '../../components/Header';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CameraScreen } from '../../components/CameraScreen';
import { ReportProblemModal } from '../../components/ReportProblemModal';
type Room = {
  id: string;
  nameKey: string;
  beforePhotoUrl: string;
  afterPhotoUrl: string | null;
  referencePhotoUrl: string;
};
export function AfterPhotosPage() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [rooms, setRooms] = useState<Room[]>([{
    id: 'soggiorno',
    nameKey: 'soggiorno',
    beforePhotoUrl: 'https://images.unsplash.com/photo-1567767292278-a4f21aa2d36e?auto=format&fit=crop&w=300&q=80',
    afterPhotoUrl: null,
    referencePhotoUrl: 'https://images.unsplash.com/photo-1567767292278-a4f21aa2d36e?auto=format&fit=crop&w=300&q=80'
  }, {
    id: 'cucina',
    nameKey: 'cucina',
    beforePhotoUrl: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=300&q=80',
    afterPhotoUrl: null,
    referencePhotoUrl: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=300&q=80'
  }, {
    id: 'bagno',
    nameKey: 'bagno',
    beforePhotoUrl: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=300&q=80',
    afterPhotoUrl: null,
    referencePhotoUrl: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=300&q=80'
  }, {
    id: 'camera1',
    nameKey: 'camera1',
    beforePhotoUrl: 'https://images.unsplash.com/photo-1616594039964-40891a909d99?auto=format&fit=crop&w=300&q=80',
    afterPhotoUrl: null,
    referencePhotoUrl: 'https://images.unsplash.com/photo-1616594039964-40891a909d99?auto=format&fit=crop&w=300&q=80'
  }, {
    id: 'camera2',
    nameKey: 'camera2',
    beforePhotoUrl: 'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=300&q=80',
    afterPhotoUrl: null,
    referencePhotoUrl: 'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=300&q=80'
  }, {
    id: 'ingresso',
    nameKey: 'ingresso',
    beforePhotoUrl: 'https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=300&q=80',
    afterPhotoUrl: null,
    referencePhotoUrl: 'https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=300&q=80'
  }]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const completedCount = rooms.filter(r => r.afterPhotoUrl !== null).length;
  const totalCount = rooms.length;
  const progress = completedCount / totalCount * 100;
  const isComplete = completedCount === totalCount;
  const handleOpenCamera = (room: Room) => {
    setSelectedRoom(room);
    setCameraOpen(true);
  };
  const handleCapturePhoto = (photoUrl: string) => {
    if (selectedRoom) {
      setRooms(prev => prev.map(room => room.id === selectedRoom.id ? {
        ...room,
        afterPhotoUrl: photoUrl
      } : room));
    }
    setCameraOpen(false);
    setSelectedRoom(null);
  };
  const handleCancelCamera = () => {
    setCameraOpen(false);
    setSelectedRoom(null);
  };
  const handleConfirmCompletion = () => {
    if (isComplete) {
      navigate('/fornitore/completion-confirmation');
    }
  };
  const handleReportProblem = (data: any) => {
    console.log('Problem reported:', data);
    setReportModalOpen(false);
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-620">
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 pb-32" data-id="element-621">
        {/* Page Header */}
        <div className="mb-12" data-id="element-622">
          <h1 className="text-4xl font-bold text-navy mb-2" data-id="element-623">
            {t.fornitore.afterPhotos.title}
          </h1>
        </div>

        {/* Progress Section */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-12" data-id="element-624">
          <div className="flex justify-between items-end mb-4" data-id="element-625">
            <span className="text-base font-medium text-gray-500 uppercase tracking-wider" data-id="element-626">
              {t.fornitore.afterPhotos.subtitle}
            </span>
            <span className="text-xl font-bold text-teal-deep" data-id="element-627">
              {t.fornitore.afterPhotos.progress.replace('{current}', completedCount.toString()).replace('{total}', totalCount.toString())}
            </span>
          </div>
          <div className="h-4 bg-gray-100 rounded-full overflow-hidden" data-id="element-628">
            <motion.div className="h-full bg-teal-deep rounded-full" initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-629" />
          </div>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-630">
          {rooms.map(room => <div key={room.id} className="space-y-3" data-id="element-631">
              <Card className="p-5 bg-white border border-gray-200" data-id="element-632">
                {/* Room Name */}
                <h3 className="text-base font-semibold text-navy mb-4" data-id="element-633">
                  {t.fornitore.beforePhotos.rooms[room.nameKey as keyof typeof t.fornitore.beforePhotos.rooms]}
                </h3>

                {/* Before/After Photos Side by Side */}
                <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-634">
                  {/* Before Photo (Left) */}
                  <div className="space-y-2" data-id="element-635">
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wide" data-id="element-636">
                      {t.fornitore.afterPhotos.before}
                    </span>
                    <div className="aspect-[4/3] rounded-lg overflow-hidden border border-gray-200" data-id="element-637">
                      <img src={room.beforePhotoUrl} alt="Before" className="w-full h-full object-cover" data-id="element-638" />
                    </div>
                  </div>

                  {/* After Photo (Right) */}
                  <div className="space-y-2" data-id="element-639">
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wide" data-id="element-640">
                      {t.fornitore.afterPhotos.after}
                    </span>
                    <motion.div whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => handleOpenCamera(room)} className="cursor-pointer" data-id="element-641">
                      <div className={`aspect-[4/3] rounded-lg overflow-hidden group transition-all duration-300 ${room.afterPhotoUrl ? 'border-2 border-teal-deep' : 'border-2 border-dashed border-gray-300 hover:border-teal-deep/50'}`} data-id="element-642">
                        <AnimatePresence mode="wait" data-id="element-643">
                          {room.afterPhotoUrl ? <motion.div key="taken" initial={{
                        opacity: 0,
                        scale: 0.8
                      }} animate={{
                        opacity: 1,
                        scale: 1
                      }} className="relative w-full h-full" data-id="element-644">
                              <img src={room.afterPhotoUrl} alt="After" className="w-full h-full object-cover" data-id="element-645" />
                              <div className="absolute top-2 right-2 bg-teal-deep text-white p-1.5 rounded-full shadow-lg" data-id="element-646">
                                <Check className="w-4 h-4" data-id="element-647" />
                              </div>
                            </motion.div> : <motion.div key="empty" initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} className="w-full h-full bg-gray-50 flex flex-col items-center justify-center text-gray-500" data-id="element-648">
                              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-2 shadow-sm group-hover:scale-110 transition-transform" data-id="element-649">
                                <Camera className="w-5 h-5 text-gray-600" data-id="element-650" />
                              </div>
                              <span className="text-xs font-medium" data-id="element-651">
                                {t.fornitore.afterPhotos.takePhoto}
                              </span>
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Tip */}
                <p className="text-sm text-gray-500 text-center italic" data-id="element-652">
                  {t.fornitore.afterPhotos.tip}
                </p>
              </Card>
            </div>)}
        </div>
      </main>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:p-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-40" data-id="element-653">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4" data-id="element-654">
          <button onClick={() => setReportModalOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-lg text-red-600 hover:bg-red-50 transition-colors border-2 border-red-500" data-id="element-655">
            <AlertCircle className="w-6 h-6 text-red-600" data-id="element-656" />
            <span className="hidden md:inline text-red-600" data-id="element-657">
              {t.fornitore.afterPhotos.reportProblem}
            </span>
            <span className="md:hidden text-red-600" data-id="element-658">Segnala</span>
          </button>

          <Button onClick={handleConfirmCompletion} disabled={!isComplete} className={`flex-1 max-w-md text-lg ${isComplete ? '' : 'opacity-40 cursor-not-allowed'}`} data-id="element-659">
            {t.fornitore.afterPhotos.confirmCompletion}
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-660" />
          </Button>
        </div>
      </div>

      {/* Camera Screen */}
      {selectedRoom && <CameraScreen isOpen={cameraOpen} roomName={t.fornitore.beforePhotos.rooms[selectedRoom.nameKey as keyof typeof t.fornitore.beforePhotos.rooms]} referencePhotoUrl={selectedRoom.referencePhotoUrl} onCapture={handleCapturePhoto} onCancel={handleCancelCamera} data-id="element-661" />}

      {/* Report Problem Modal */}
      <ReportProblemModal isOpen={reportModalOpen} onClose={() => setReportModalOpen(false)} onSubmit={handleReportProblem} data-id="element-662" />
    </div>;
}